import React from 'react';
import { useTranslation } from 'react-i18next';
import config from '@/config.json';
import { themes } from '@/themes';
import {
  GridItemHeadline,
  GridItemText,
  GridList,
  GridListItem,
  Section,
  SectionBox,
  SectionHeadline,
  SectionWrapper,
} from './styled';

const DefaultTemplate = () => {
  const { t } = useTranslation();
  const theme = themes[config?.TEMPLATE ?? 'default'][config.THEME ?? 'dark'];

  const getColor = (() => {
    switch (config?.DESCRIPTOR_NAME) {
      case 'PROTECT247.PRO':
        return '#E9EAEE';
      case 'PROTECT247.LIVE':
        return 'linear-gradient(180deg, #FFF 0%, #E4F9F0 55.21%, rgba(240, 250, 252, 0.00) 100%), #E8F7EC';
      case 'PROTECT247.SHOP':
      case 'MUSTREADZ.ONLINE':
        return '#ECEEF5';
      case 'PROTECT247.MOBI':
        return '#ADCBB6';
      case 'PROTECT247.LINK':
      case 'MUSTREADZ.SHOP':
        return '#E7E7F2';
      case 'KIDGY.MOBI':
        return '#E3EEF6';
      case 'SPOTTRACK.PRO':
      case 'GPF.SBSCRIL.CC':
      case 'MDTN.BLLX.CC':
        return '#E3ECF6';
      case 'SPOTTRACK.INFO':
      case 'MUSTREADZ.STORE':
      case 'GPTF.SBSCRIL.CC':
      case 'TRSP.BLLX.CC':
        return '#E0F3F4';
      case 'SPOTTRACK.SHOP':
      case 'MUSTREADZ.SITE':
      case 'GRP.SBSCRIL.CC':
      case 'SPTR.BLLX.CC':
        return '#ECECFA';
      case 'SEARQLE.PRO':
      case 'GRPT.SBSCRIL.CC':
      case 'MVIEW.APP':
      case 'MNP.BLLX.CC':
        return '#FAF6EF';
      case 'SEARQLE.INFO':
      case 'VINR.BLLX.CC':
      case 'GRPF.SBSCRIL.CC':
        return '#DBEFFF';
      case 'SEARQLE.STORE':
      case 'VI.BLLX.CC':
        return '#ECECFF';
      case 'SEARQLE.CLICK':
      case 'VNR.BLLX.CC':
      case 'MSPC.BLLX.CC':
        return '#E7F4F5';
      case 'SEARQLE.SITE':
      case 'MUSTREADZ.CFD':
      case 'VR.BLLX.CC':
        return '#E2E3F5';
      case 'PLANMD-TP.PRO':
      case 'TOP-MEDITATION-PLAN.SBS':
      case 'HL.SBSCRIL.CC':
        return 'linear-gradient(180deg, #F9F7FA 0%, #FBEDE2 100%)';
      case 'TOPPLAN.PRO':
      case 'HM.SBSCRIL.CC':
        return '#FFFAF0';
      case 'TPLANMDT.PRO':
      case 'HLM.SBSCRIL.CC':
        return '#FAFBFC';
      case 'YOUMDPLN.PRO':
      case 'Y-MEDITN.PRO':
      case 'YRMDTN.PRO':
      case 'HEALTH-MEDITATION.ONLINE':
      case 'VNP.BLLX.CC':
      case 'DISCOWERYCHNL.SPACE':
      case 'KIDSECURED.ONLINE':
        return '#F0FCF7';
      case 'HLT.SBSCRIL.CC':
        return '#F2DDCF';
      case 'HTM.SBSCRIL.CC':
        return '#FDF9F6';
      case 'DLY-HLPR.PRO':
      case 'YOURMEDITATIONPLAN.CFD':
        return '#F8F7FE';
      case 'DAYHPR.PRO':
      case 'MDGUIDE4U.FYI':
      case 'VP.BLLX.CC':
        return '#F8F7FE';
      case 'VNL.BLLX.CC':
      case 'HBOPRIMEVIDEO.INFO':
      case 'PARAMAUNTLIVE.LIVE':
        return '#F4F8F8';
      case 'DAILY-MDH.PRO':
      case 'SERVS.APP':
      case 'VL.BLLX.CC':
      case 'MYKIDSGUARD.CFD':
        return 'linear-gradient(180deg, #FFF 0.11%, #F3FBFE 99.89%)';
      default:
        return theme.backgroundColorDarker;
    }
  })();

  return (
    <Section id="faq" customBG={getColor}>
      <SectionWrapper>
        <SectionBox>
          <SectionHeadline>{t(`questionSection.billing.headline`)}</SectionHeadline>
          <GridList column={3}>
            <GridListItem>
              <GridItemHeadline>
                {t(`questionSection.billing.elementHeadline1`, {
                  descriptor: config.TRADE_NAME,
                  descriptor_domain: config.DESCRIPTOR_DOMAIN,
                })}
              </GridItemHeadline>
              <GridItemText>
                {t(`questionSection.billing.elementDescription1`, {
                  descriptor: config.TRADE_NAME,
                  descriptor_domain: config.DESCRIPTOR_DOMAIN,
                })}
              </GridItemText>
            </GridListItem>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.billing.elementHeadline2`)}</GridItemHeadline>
              <GridItemText>{t(`questionSection.billing.elementDescription2`)}</GridItemText>
            </GridListItem>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.billing.elementHeadline3`)}</GridItemHeadline>
              <GridItemText>{t(`questionSection.billing.elementDescription3`)}</GridItemText>
            </GridListItem>
          </GridList>
        </SectionBox>
        <SectionBox>
          <SectionHeadline>{t(`questionSection.others.headline`)}</SectionHeadline>
          <GridList column={4}>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.others.elementHeadline1`)}</GridItemHeadline>
              <GridItemText>
                {t(`questionSection.others.elementDescription1`, {
                  descriptor: config.TRADE_NAME,
                  descriptor_domain: config.DESCRIPTOR_DOMAIN,
                })}
              </GridItemText>
            </GridListItem>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.others.elementHeadline2`)}</GridItemHeadline>
              <GridItemText>{t(`questionSection.others.elementDescription2`)}</GridItemText>
            </GridListItem>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.others.elementHeadline3`)}</GridItemHeadline>
              <GridItemText
                dangerouslySetInnerHTML={{
                  __html: t('questionSection.others.elementDescription3', {
                    descriptor_email: config.MAILER_SUPPORT_EMAIL,
                    descriptor: config.TRADE_NAME,
                    descriptor_domain: config.DESCRIPTOR_DOMAIN,
                  }),
                }}
              />
            </GridListItem>
            <GridListItem>
              <GridItemHeadline>{t(`questionSection.others.elementHeadline4`)}</GridItemHeadline>
              <GridItemText>{t(`questionSection.others.elementDescription4`)}</GridItemText>
            </GridListItem>
          </GridList>
        </SectionBox>
      </SectionWrapper>
    </Section>
  );
};

export default DefaultTemplate;
